import { patientIntakeApi } from "./patientIntakeApi";

const MAX_CONCURRENT_UPLOADS = 3;

export interface UploadProgress {
  current: number;
  total: number;
  currentPhoto: string;
  status: "preparing" | "uploading" | "registering" | "completed" | "error";
  failedPhotos: string[];
  uploadSpeed?: number;
  estimatedTimeRemaining?: number;
  totalSize?: number;
  uploadedSize?: number;
  startTime?: number;
  individualProgress?: Record<string, number>;
}

export interface BatchUploadResult {
  success: boolean;
  uploadedPhotos: Record<string, string>;
  failedPhotos: string[];
  mediaIds: Record<string, string>;
  errors: Record<string, string>;
}

export interface QueuedPhotoMetadata {
  photoPosition?: string;
  photoSequence?: number;
  replaceMediaId?: string;
  originalFilename?: string;
}

interface QueuedPhoto {
  id: string;
  file: File;
  previewUrl: string;
  metadata: QueuedPhotoMetadata;
  dataUrl?: string;
}

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return "Unknown error";
  }
};

export class BatchUploadService {
  private photos: Map<string, QueuedPhoto> = new Map();
  private onProgress: (progress: UploadProgress) => void;

  constructor(onProgress: (progress: UploadProgress) => void) {
    this.onProgress = onProgress;
  }

  addPhoto(
    photoId: string,
    source: string | File,
    metadata: QueuedPhotoMetadata = {},
  ): string {
    if (this.photos.has(photoId)) {
      this.removePhoto(photoId);
    }

    const normalizedMetadata: QueuedPhotoMetadata = {
      photoPosition: metadata.photoPosition ?? photoId,
      photoSequence: metadata.photoSequence,
      replaceMediaId: metadata.replaceMediaId,
      originalFilename:
        metadata.originalFilename ||
        (source instanceof File ? source.name : `${photoId}.jpg`),
    };

    const queuedPhoto = this.createQueuedPhoto(
      photoId,
      source,
      normalizedMetadata,
    );

    this.photos.set(photoId, queuedPhoto);
    return queuedPhoto.previewUrl;
  }

  updatePhotoMetadata(
    photoId: string,
    metadata: Partial<QueuedPhotoMetadata>,
  ): void {
    const existing = this.photos.get(photoId);
    if (!existing) {
      return;
    }

    existing.metadata = {
      ...existing.metadata,
      ...metadata,
    };
    this.photos.set(photoId, existing);
  }

  getPhotoCount(): number {
    return this.photos.size;
  }

  hasPhoto(photoId: string): boolean {
    return this.photos.has(photoId);
  }

  removePhoto(photoId: string): void {
    const existing = this.photos.get(photoId);
    if (!existing) {
      return;
    }

    URL.revokeObjectURL(existing.previewUrl);
    this.photos.delete(photoId);
  }

  async uploadAllPhotos(
    email: string,
    formUid?: string,
  ): Promise<BatchUploadResult> {
    const result: BatchUploadResult = {
      success: true,
      uploadedPhotos: {},
      failedPhotos: [],
      mediaIds: {},
      errors: {},
    };

    const photoEntries = Array.from(this.photos.entries());
    const totalPhotos = photoEntries.length;

    if (totalPhotos === 0) {
      return result;
    }

    let actualFormUid = formUid;

    try {
      if (!actualFormUid) {
        const formResponse = await patientIntakeApi.getIntakeFormByEmail(email);
        actualFormUid = formResponse.data.formUid;
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      result.success = false;
      result.failedPhotos = photoEntries.map(([photoId]) => photoId);
      result.errors = Object.fromEntries(
        result.failedPhotos.map((id) => [id, errorMessage]),
      );

      this.emitProgress({
        status: "error",
        total: totalPhotos,
        current: 0,
        currentPhoto: "Unable to determine intake form",
        failedPhotos: result.failedPhotos,
        startTime: Date.now(),
      });

      return result;
    }

    if (!actualFormUid) {
      const errorMessage = "Unable to determine intake form UID";
      result.success = false;
      result.failedPhotos = photoEntries.map(([photoId]) => photoId);
      result.errors = Object.fromEntries(
        result.failedPhotos.map((id) => [id, errorMessage]),
      );

      this.emitProgress({
        status: "error",
        total: totalPhotos,
        current: 0,
        currentPhoto: "Form UID missing",
        failedPhotos: result.failedPhotos,
        startTime: Date.now(),
      });

      return result;
    }

    const totalSize = photoEntries.reduce(
      (acc, [, item]) => acc + item.file.size,
      0,
    );
    const startTime = Date.now();

    this.emitProgress({
      status: "preparing",
      total: totalPhotos,
      current: 0,
      currentPhoto: "Preparing upload...",
      failedPhotos: [],
      startTime,
      totalSize,
      uploadedSize: 0,
    });

    let presignResponse: Awaited<
      ReturnType<typeof patientIntakeApi.getBatchPhotoUploadUrls>
    >;

    try {
      presignResponse = await patientIntakeApi.getBatchPhotoUploadUrls(
        actualFormUid,
        photoEntries.map(([photoId, item]) => ({
          clientReference: photoId,
          mimeType: item.file.type || "image/jpeg",
          size: item.file.size,
          originalFilename:
            item.metadata.originalFilename ||
            item.file.name ||
            `${photoId}.jpg`,
        })),
      );
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      result.success = false;
      result.failedPhotos = photoEntries.map(([photoId]) => photoId);
      result.errors = Object.fromEntries(
        result.failedPhotos.map((id) => [id, errorMessage]),
      );

      this.emitProgress({
        status: "error",
        total: totalPhotos,
        current: 0,
        currentPhoto: "Failed to generate upload URLs",
        failedPhotos: result.failedPhotos,
        startTime,
        totalSize,
        uploadedSize: 0,
      });

      return result;
    }

    const uploadMap = new Map(
      presignResponse.data.uploads.map((upload) => [
        upload.clientReference,
        upload,
      ]),
    );

    const successfulUploads: Array<{
      photoId: string;
      file: File;
      metadata: QueuedPhotoMetadata;
      previewUrl: string;
      dataUrl?: string;
      uploadBucket: string;
      storagePath: string;
    }> = [];

    const failedPhotos: string[] = [];
    const errors: Record<string, string> = {};

    let completed = 0;
    let uploadedSize = 0;

    const updateProgress = (
      status: UploadProgress["status"],
      currentPhoto: string,
    ) => {
      const elapsedSeconds = (Date.now() - startTime) / 1000;
      const uploadSpeed =
        uploadedSize > 0 && elapsedSeconds > 0
          ? uploadedSize / elapsedSeconds
          : undefined;
      const remainingSize = totalSize - uploadedSize;
      const estimatedTimeRemaining =
        uploadSpeed && uploadSpeed > 0
          ? remainingSize / uploadSpeed
          : undefined;

      this.emitProgress({
        status,
        total: totalPhotos,
        current: completed,
        currentPhoto,
        failedPhotos,
        startTime,
        totalSize,
        uploadedSize,
        uploadSpeed,
        estimatedTimeRemaining,
      });
    };

    let currentIndex = 0;
    const concurrency = Math.min(MAX_CONCURRENT_UPLOADS, totalPhotos);

    const workers = Array.from({ length: concurrency }).map(async () => {
      while (true) {
        const index = currentIndex;
        currentIndex += 1;

        if (index >= photoEntries.length) {
          break;
        }

        const [photoId, item] = photoEntries[index];
        const uploadDetails = uploadMap.get(photoId);

        if (!uploadDetails) {
          failedPhotos.push(photoId);
          errors[photoId] = "Missing upload destination";
          completed += 1;
          updateProgress("error", `Upload destination missing for ${photoId}`);
          continue;
        }

        try {
          updateProgress("uploading", `Uploading ${photoId}...`);
          await patientIntakeApi.uploadPhotoToCloud(
            uploadDetails.uploadUrl,
            item.file,
          );

          successfulUploads.push({
            photoId,
            file: item.file,
            metadata: item.metadata,
            previewUrl: item.previewUrl,
            dataUrl: item.dataUrl,
            uploadBucket: uploadDetails.bucketName,
            storagePath: uploadDetails.storagePath,
          });

          uploadedSize += item.file.size;
        } catch (error) {
          const errorMessage = getErrorMessage(error);
          failedPhotos.push(photoId);
          errors[photoId] = errorMessage;
        } finally {
          completed += 1;
          updateProgress("uploading", `Processed ${photoId}`);
        }
      }
    });

    await Promise.all(workers);

    if (successfulUploads.length === 0) {
      result.success = false;
      result.failedPhotos = failedPhotos;
      result.errors = errors;

      updateProgress(
        "error",
        failedPhotos.length === totalPhotos
          ? "All uploads failed"
          : "Some uploads failed",
      );

      return result;
    }

    updateProgress("registering", "Registering uploaded media...");

    let registrationResponse: Awaited<
      ReturnType<typeof patientIntakeApi.registerMediaBatch>
    >;

    try {
      registrationResponse = await patientIntakeApi.registerMediaBatch(
        actualFormUid,
        successfulUploads.map((upload) => ({
          clientReference: upload.photoId,
          mediaType: "photo",
          s3BucketName: upload.uploadBucket,
          s3StoragePath: upload.storagePath,
          originalFilename:
            upload.metadata.originalFilename ||
            upload.file.name ||
            `${upload.photoId}.jpg`,
          mimeType: upload.file.type || "image/jpeg",
          fileSize: upload.file.size,
          photoPosition: upload.metadata.photoPosition,
          photoSequence: upload.metadata.photoSequence,
          replaceMediaId: upload.metadata.replaceMediaId,
        })),
      );
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      successfulUploads.forEach((upload) => {
        failedPhotos.push(upload.photoId);
        errors[upload.photoId] = errorMessage;
      });

      result.success = false;
      result.failedPhotos = failedPhotos;
      result.errors = errors;

      updateProgress("error", "Failed to register uploaded media");
      return result;
    }

    const registeredMap = new Map(
      registrationResponse.data.items.map((item) => [
        item.clientReference,
        item.media,
      ]),
    );

    successfulUploads.forEach((upload) => {
      const registered = registeredMap.get(upload.photoId);

      if (!registered) {
        failedPhotos.push(upload.photoId);
        errors[upload.photoId] = "Registration response missing for photo";
        return;
      }

      const finalUrl =
        registered.downloadUrl ?? upload.dataUrl ?? upload.previewUrl;

      result.uploadedPhotos[upload.photoId] = finalUrl;
      result.mediaIds[upload.photoId] = registered.id;

      if (registered.downloadUrl || upload.dataUrl) {
        URL.revokeObjectURL(upload.previewUrl);
      }

      this.photos.delete(upload.photoId);
    });

    result.failedPhotos = failedPhotos;
    result.errors = errors;
    result.success = failedPhotos.length === 0;

    updateProgress(
      result.success ? "completed" : "error",
      result.success ? "Upload completed" : "Upload completed with errors",
    );

    return result;
  }

  cleanup(): void {
    this.photos.forEach((photo) => {
      URL.revokeObjectURL(photo.previewUrl);
    });
    this.photos.clear();
  }

  private emitProgress(progress: UploadProgress): void {
    try {
      this.onProgress(progress);
    } catch (error) {
      console.error("Failed to emit upload progress", error);
    }
  }

  private createQueuedPhoto(
    photoId: string,
    source: string | File,
    metadata: QueuedPhotoMetadata,
  ): QueuedPhoto {
    if (typeof source === "string") {
      const file = this.dataURLToFile(
        source,
        metadata.originalFilename ?? `${photoId}.jpg`,
      );
      const blob = this.dataURLToBlob(source);
      const previewUrl = URL.createObjectURL(blob);

      return {
        id: photoId,
        file,
        previewUrl,
        metadata,
        dataUrl: source,
      };
    }

    const previewUrl = URL.createObjectURL(source);

    return {
      id: photoId,
      file: source,
      previewUrl,
      metadata,
    };
  }

  private dataURLToFile(dataUrl: string, filename: string): File {
    const arr = dataUrl.split(",");
    const mime = arr[0]?.match(/:(.*?);/)?.[1] || "image/jpeg";
    const bstr = atob(arr[1] ?? "");
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  }

  private dataURLToBlob(dataUrl: string): Blob {
    const arr = dataUrl.split(",");
    const mime = arr[0]?.match(/:(.*?);/)?.[1] || "image/jpeg";
    const bstr = atob(arr[1] ?? "");
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new Blob([u8arr], { type: mime });
  }
}
